import { DialogTrigger } from '@ibsys/kit';
import { StarIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';
import { IconButton } from '@/shared/ui';
import { PinUnsavedPresetDialog } from './PinUnsavedPresetDialog';

type Props = {
  /** Название пресета — для заголовка предупреждения */
  presetName: string;
  /** Пресет закреплён в левом меню */
  isPinned: boolean;
  /** У пресета есть несохранённая донастройка */
  hasUnsavedChanges: boolean;
  /** Донастройку можно записать в пресет */
  canSaveChanges: boolean;
  /** Закрепить или открепить пресет как есть */
  onSetPinned: (pinned: boolean) => void;
  /** Записать донастройку в пресет и закрепить его */
  onSaveAndPin: () => void;
};

/**
 * Кнопка закрепления пресета в левом меню и его открепления.
 *
 * Незакреплённый пресет с несохранёнными изменениями сразу не закрепляется: кнопка открывает
 * предупреждение с выбором, переносить ли донастройку в пресет. Без изменений пресет закрепляется
 * сразу; открепление предупреждения не показывает.
 */
export const PresetPinButton: FC<Props> = ({
  presetName,
  isPinned,
  hasUnsavedChanges,
  canSaveChanges,
  onSetPinned,
  onSaveAndPin,
}) => {
  const { t } = useTranslation('tablePresets');

  const buttonProps = {
    size: 'icon',
    className: 'shrink-0',
    label: isPinned ? t('unpinFromMenu') : t('pinToMenu'),
    'aria-pressed': isPinned,
    icon: <StarIcon className={cn('size-4', isPinned && 'fill-current')} />,
  } as const;

  if (isPinned || !hasUnsavedChanges) {
    return <IconButton {...buttonProps} onClick={() => onSetPinned(!isPinned)} />;
  }

  return (
    <PinUnsavedPresetDialog
      trigger={<IconButton {...buttonProps} trigger={DialogTrigger} />}
      presetName={presetName}
      canSaveChanges={canSaveChanges}
      onPinWithoutChanges={() => onSetPinned(true)}
      onSaveAndPin={onSaveAndPin}
    />
  );
};
